import React, { useEffect, useRef, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';

const MAPS_LIBRARIES: ('places')[] = ['places'];

interface PlaceResult {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (result: PlaceResult) => void;
  style: React.CSSProperties;
  placeholder: string;
  required?: boolean;
}

interface Suggestion {
  placeId: string;
  text: string;
}

const AutocompleteInner: React.FC<Props> = ({ value, onChange, onPlaceSelected, style, placeholder, required }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = (input: string) => {
    if (!input || input.length < 2) { setSuggestions([]); setOpen(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { suggestions: raw } = await (google.maps.places as any).AutocompleteSuggestion
          .fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: ['us'],
          });
        setSuggestions(raw.map((s: any) => ({
          placeId: s.placePrediction?.placeId ?? '',
          text: s.placePrediction?.text?.text ?? '',
        })).filter((s: Suggestion) => s.placeId));
        setOpen(true);
      } catch {
        setSuggestions([]); setOpen(false);
      }
    }, 250);
  };

  const handleSelect = async (s: Suggestion) => {
    onChange(s.text);
    setSuggestions([]); setOpen(false);
    try {
      const place = new (google.maps.places as any).Place({ id: s.placeId });
      await place.fetchFields({ fields: ['addressComponents'] });
      const components: any[] = place.addressComponents ?? [];
      let streetNumber = '', route = '', city = '', state = '', zipCode = '';
      for (const c of components) {
        const types: string[] = c.types ?? [];
        if (types.includes('street_number')) streetNumber = c.longText ?? '';
        else if (types.includes('route')) route = c.longText ?? '';
        else if (types.includes('locality')) city = c.longText ?? '';
        else if (types.includes('administrative_area_level_1')) state = c.shortText ?? '';
        else if (types.includes('postal_code')) zipCode = c.longText ?? '';
      }
      onPlaceSelected({ address: `${streetNumber} ${route}`.trim(), city, state, zipCode });
    } catch {}
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={placeholder}
        style={style}
        value={value}
        onChange={e => { onChange(e.target.value); fetchSuggestions(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        required={required}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul style={dropdownStyle}>
          {suggestions.map(s => (
            <li key={s.placeId} style={itemStyle} onMouseDown={() => handleSelect(s)}>
              {s.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AddressAutocompleteField: React.FC<Props> = (props) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    libraries: MAPS_LIBRARIES,
    language: 'en',
  });

  if (!isLoaded) {
    return (
      <input
        type="text"
        placeholder={props.placeholder}
        style={props.style}
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        required={props.required}
      />
    );
  }

  return <AutocompleteInner {...props} />;
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
  background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)', listStyle: 'none',
  margin: 0, padding: '4px 0', maxHeight: 240, overflowY: 'auto',
};

const itemStyle: React.CSSProperties = {
  padding: '10px 16px', fontSize: 13, cursor: 'pointer', color: '#333',
};

export default AddressAutocompleteField;
